import React, { useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  FileUpload,
  Description,
  Visibility,
  Edit,
  Save,
  Cancel,
  Schedule,
  Sync,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { importService } from '../../../services/importService';

interface ImportSysupcircuitosModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedData {
  valid: boolean;
  duplicate: boolean;
  validationErrors?: string[];
  importStatus?: 'pending' | 'importing' | 'success' | 'error';
  importError?: string;
  [key: string]: any;
}

interface ImportStats {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
}

interface ImportProgress {
  nome: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
}

const steps = [
  'Configuração da API',
  'Análise e Importação'
];

export const ImportSysupcircuitosModal: React.FC<ImportSysupcircuitosModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState('https://www.seusite.com.br/admin/addons/sysup/apiv2/listarCircuitosFtth');
  const [apiToken, setApiToken] = useState('9f4c8db4c60a93f1a3a77a9dd11b2f5c49c634b9dcf32776f79c4fc343a3b07d');
  const [parsedData, setParsedData] = useState<ParsedData[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0
  });
  // Estado de edição removido - interface simplificada
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ [key: number]: ImportProgress }>({});
  const [importCompleted, setImportCompleted] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Remover referência ao file input - agora usando API

  const getFieldValidationErrors = (row: ParsedData): { [key: string]: boolean } => {
    const fieldErrors: { [key: string]: boolean } = {};

    // Verificar campos obrigatórios
    if (!row.identificacao) fieldErrors.identificacao = true;
    if (!row.cto_nome) fieldErrors.cto_nome = true;

    return fieldErrors;
  };

  const parseApiData = (circuitosData: any[]): ParsedData[] => {
    try {
      const data: ParsedData[] = [];

      circuitosData.forEach((circuito) => {
        const circuitoData: ParsedData = {
          id: circuito.id || '',
          identificacao: circuito.identificacao || '',
          pontos: circuito.pontos || '',
          cto_nome: circuito.cto_nome || '',
          endereco: circuito.endereco || '',
          situacao: circuito.situacao || '',
          valid: true,
          duplicate: false,
          validationErrors: []
        };

        // Validações básicas
        let isValid = true;
        let validationErrors: string[] = [];

        if (!circuitoData.identificacao) {
          isValid = false;
          validationErrors.push('Identificação obrigatória');
        }

        if (!circuitoData.cto_nome) {
          isValid = false;
          validationErrors.push('Nome do CTO obrigatório');
        }

        circuitoData.valid = isValid;
        circuitoData.validationErrors = validationErrors;

        data.push(circuitoData);
      });

      return data;
    } catch (error) {
      console.error('Erro ao processar dados da API:', error);
      throw new Error('Erro ao processar dados da API');
    }
  };



  const handleApiUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiUrl(event.target.value);
    setError(null);
  };

  const handleApiTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiToken(event.target.value);
    setError(null);
  };

  const handleFetchData = async () => {
    if (!apiUrl.trim()) {
      setError('URL da API é obrigatória');
      return;
    }

    if (!apiToken.trim()) {
      setError('Token da API é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fazer requisição para a API externa
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const apiData = await response.json();

      // Verificar se a resposta contém dados de circuitos
      let circuitosData: any[];

      if (Array.isArray(apiData)) {
        circuitosData = apiData;
      } else if (apiData && typeof apiData === 'object') {
        // Verificar se existe a propriedade 'data' (formato padrão da API)
        if (apiData.data && Array.isArray(apiData.data)) {
          circuitosData = apiData.data;
          console.log(`Array de circuitos encontrado na propriedade: data (${circuitosData.length} circuitos)`);
        } else {
          throw new Error('Propriedade "data" não encontrada na resposta da API');
        }
      } else {
        throw new Error('Formato de resposta inválido da API');
      }

      if (!Array.isArray(circuitosData) || circuitosData.length === 0) {
        throw new Error('Nenhum circuito encontrado na resposta da API');
      }

      // Processar dados da API
      const parsedData = parseApiData(circuitosData);

      // Detectar duplicados (baseado no nome do circuito)
      const duplicateNames = new Set();
      const finalData = parsedData.map(item => {
        const identificacao = item.identificacao?.trim();
        if (identificacao && duplicateNames.has(identificacao)) {
          return { ...item, duplicate: true };
        }
        if (identificacao) {
          duplicateNames.add(identificacao);
        }
        return item;
      });

      // Ordenar dados: primeiro válidos não-duplicados, depois inválidos, por último duplicados
      const sortedData = finalData.sort((a, b) => {
        // Primeiro critério: válidos não-duplicados primeiro
        if (a.valid && !a.duplicate && (!b.valid || b.duplicate)) return -1;
        if (!a.valid || a.duplicate) {
          if (b.valid && !b.duplicate) return 1;
        }

        // Segundo critério: inválidos antes de duplicados
        if (!a.valid && b.duplicate) return -1;
        if (a.duplicate && !b.valid) return 1;

        // Terceiro critério: ordenar por nome
        return (a.identificacao || '').localeCompare(b.identificacao || '');
      });

      setParsedData(sortedData);
      // Selecionar por padrão os válidos e não duplicados
      const defaultSelected = new Set<number>();
      sortedData.forEach((item, idx) => {
        if (item.valid && !item.duplicate) defaultSelected.add(idx);
      });
      setSelectedRows(defaultSelected);
      setImportStats({
        total: finalData.length,
        valid: finalData.filter(item => item.valid && !item.duplicate).length,
        invalid: finalData.filter(item => !item.valid).length,
        duplicates: finalData.filter(item => item.duplicate).length
      });

      setActiveStep(1);
      toast.success(`Dados obtidos com sucesso! ${finalData.length} circuitos encontrados.`);
    } catch (err: any) {
      console.error('Erro ao buscar dados da API:', err);
      setError(err.message || 'Erro ao conectar com a API. Verifique a URL e o token.');
    } finally {
      setLoading(false);
    }
  };

  // Funções de edição removidas - interface simplificada

  const handleStartImport = async () => {
    setImporting(true);
    setImportProgress({});
    setImportCompleted(false);

    try {
      // Filtrar apenas dados válidos, não duplicados e selecionados
      const validData = parsedData.filter((item, index) => item.valid && !item.duplicate && selectedRows.has(index));

      if (validData.length === 0) {
        throw new Error('Nenhum circuito válido para importar');
      }

      let successCount = 0;
      let errorCount = 0;

      // Processar cada circuito individualmente
      for (let i = 0; i < validData.length; i++) {
        const circuito = validData[i];
        const originalIndex = parsedData.findIndex(p => p === circuito);

        // Atualizar status para "importando"
        setImportProgress(prev => ({
          ...prev,
          [originalIndex]: {
            nome: circuito.identificacao,
            status: 'processando'
          }
        }));

        try {
          // Usar o serviço de importação
          const result = await importService.importCircuitos({
            tableData: [{
              ...circuito
            }]
          });

          if (result.success && result.data.summary.success > 0) {
            // Sucesso
            setImportProgress(prev => ({
              ...prev,
              [originalIndex]: {
                nome: circuito.identificacao,
                status: 'sucesso'
              }
            }));
            successCount++;
          } else {
            // Erro retornado pela API
            const errorMsg = result.data.results?.[0]?.erro || 'Erro desconhecido';
            setImportProgress(prev => ({
              ...prev,
              [originalIndex]: {
                nome: circuito.identificacao,
                status: 'erro',
                erro: errorMsg
              }
            }));
            errorCount++;
          }
        } catch (err: any) {
          // Erro de requisição
          setImportProgress(prev => ({
            ...prev,
            [originalIndex]: {
              nome: circuito.identificacao,
              status: 'erro',
              erro: err.message
            }
          }));
          errorCount++;
        }

        // Pequeno delay entre requisições para não sobrecarregar
        if (i < validData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setImportCompleted(true);

      // Mostrar resultado final
      if (successCount > 0) {
        toast.success(`${successCount} circuitos importados com sucesso!`);
      }

      if (errorCount > 0) {
        toast.warning(`${errorCount} circuitos não puderam ser importados. Verifique os detalhes na tabela.`);
      }

      // Se todos foram importados com sucesso, chamar onSuccess
      if (errorCount === 0) {
        setTimeout(() => onSuccess(), 2000);
      }

    } catch (err: any) {
      console.error('Erro ao importar circuitos:', err);
      setError(err.message || 'Erro ao importar circuitos. Tente novamente.');
      toast.error(err.message || 'Erro ao importar circuitos');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setApiUrl('');
    setApiToken('');
    setParsedData([]);
    setError(null);
    setLoading(false);
    // setEditingData removido
    setImporting(false);
    setImportProgress({});
    setImportCompleted(false);
    setSelectedRows(new Set());
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Conecte-se à API externa para importar dados de circuitos
      </Typography>

      <Card sx={{ mt: 3, p: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CloudUpload sx={{ fontSize: '4rem', color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Configuração da API
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Informe a URL e token de acesso da API externa
              </Typography>
            </Box>

            <TextField
              label="URL da API"
              placeholder="https://api.exemplo.com/circuitos"
              value={apiUrl}
              onChange={handleApiUrlChange}
              fullWidth
              variant="outlined"
              helperText="URL completa da API que retorna dados de circuitos"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <CloudUpload color="action" />
                  </Box>
                )
              }}
            />

            <TextField
              label="Token de Acesso"
              placeholder="seu_token_de_acesso_aqui"
              value={apiToken}
              onChange={handleApiTokenChange}
              fullWidth
              variant="outlined"
              type="password"
              helperText="Token Bearer para autenticação na API"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Description color="action" />
                  </Box>
                )
              }}
            />

            {apiUrl && apiToken && (
              <Box sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'success.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Typography variant="body2" color="success.dark">
                  ✓ Configuração da API preenchida. Clique em "Buscar Dados" para continuar.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const isRowSelectable = (row: ParsedData) => row.valid && !row.duplicate;

  const totalSelectable = useMemo(() => parsedData.filter(isRowSelectable).length, [parsedData]);
  const selectedCount = useMemo(() => Array.from(selectedRows).length, [selectedRows]);
  const allSelected = totalSelectable > 0 && selectedCount === totalSelectable;
  const someSelected = selectedCount > 0 && selectedCount < totalSelectable;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSet = new Set<number>();
      parsedData.forEach((row, idx) => {
        if (isRowSelectable(row)) newSet.add(idx);
      });
      setSelectedRows(newSet);
    } else {
      setSelectedRows(new Set());
    }
  };

  const toggleRowSelection = (index: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const renderAnalysisStep = () => (
    <Box>
      {/* Seleção de escritório e resumo */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Chip label={`Selecionados: ${selectedCount}`} color={selectedCount > 0 ? 'primary' : 'default'} size="small" />
                <Chip label={`Válidos: ${importStats.valid}`} color="success" size="small" />
                <Chip label={`Duplicados: ${importStats.duplicates}`} color="warning" size="small" />
                <Chip label={`Inválidos: ${importStats.invalid}`} color="error" size="small" />
                <Chip label={`Total: ${importStats.total}`} size="small" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela de dados */}
      <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ padding: '2px 6px' }}>
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  inputProps={{ 'aria-label': 'Selecionar todos os válidos' }}
                />
              </TableCell>
              <TableCell sx={{ padding: '2px 6px', fontSize: '0.75rem' }}>Status</TableCell>
              {parsedData.length > 0 && Object.keys(parsedData[0])
                .filter(key => !['valid', 'duplicate', 'validationErrors', 'isEditing'].includes(key))
                .map(key => (
                  <TableCell key={key} sx={{ minWidth: 120, padding: '2px 6px', fontSize: '0.75rem' }}>
                    {key.replace(/_/g, ' ').toUpperCase()}
                  </TableCell>
                ))}
              {(importing || Object.keys(importProgress).length > 0) && (
                <TableCell sx={{ minWidth: 120, padding: '2px 6px', fontSize: '0.75rem' }}>
                  ERRO IMPORTAÇÃO
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedData.map((row, index) => {
              return (
                <TableRow key={index}>
                  {/* Checkbox de seleção */}
                  <TableCell padding="checkbox" sx={{ padding: '1px 6px', height: '32px' }}>
                    <Checkbox
                      disabled={!isRowSelectable(row)}
                      checked={selectedRows.has(index)}
                      onChange={() => toggleRowSelection(index)}
                      inputProps={{ 'aria-label': `Selecionar circuito ${row.identificacao || index}` }}
                    />
                  </TableCell>

                  <TableCell sx={{ padding: '1px 6px', height: '32px' }}>
                    {(() => {
                      const progress = importProgress[index];

                      // Se está importando ou há progresso, mostrar status de importação
                      if (importing || progress) {
                        if (progress?.status === 'processando') {
                          return (
                            <Chip
                              icon={<Sync className="animate-spin" />}
                              label="Importando..."
                              color="info"
                              size="small"
                              sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                            />
                          );
                        } else if (progress?.status === 'sucesso') {
                          return (
                            <Chip
                              icon={<CheckCircle />}
                              label="Importado"
                              color="success"
                              size="small"
                              sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                            />
                          );
                        } else if (progress?.status === 'erro') {
                          return (
                            <Chip
                              icon={<ErrorIcon />}
                              label="Erro"
                              color="error"
                              size="small"
                              sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                              title={progress.erro}
                            />
                          );
                        } else if (importing && row.valid && !row.duplicate) {
                          return (
                            <Chip
                              icon={<Schedule />}
                              label="Aguardando"
                              color="default"
                              size="small"
                              sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                            />
                          );
                        }
                      }

                      // Status padrão de validação
                      if (row.valid) {
                        if (row.duplicate) {
                          return (
                            <Chip
                              icon={<Warning />}
                              label="Duplicado"
                              color="warning"
                              size="small"
                              sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                            />
                          );
                        } else {
                          return (
                            <Chip
                              icon={<CheckCircle />}
                              label="Válido"
                              color="success"
                              size="small"
                              sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                            />
                          );
                        }
                      } else {
                        return (
                          <Chip
                            icon={<ErrorIcon />}
                            label="Inválido"
                            color="error"
                            size="small"
                            sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '12px' } }}
                          />
                        );
                      }
                    })()}
                  </TableCell>

                  {Object.entries(row)
                    .filter(([key]) => !['valid', 'duplicate', 'validationErrors', 'isEditing'].includes(key))
                    .map(([key, value]) => {
                      const fieldErrors = getFieldValidationErrors(row);
                      const hasError = fieldErrors[key];

                      return (
                        <TableCell
                          key={key}
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            border: hasError ? '1px dashed #f44336' : 'none',
                            backgroundColor: hasError ? '#ffebee' : 'transparent',
                            padding: '1px 6px',
                            fontSize: '0.75rem',
                            height: '32px',
                            lineHeight: '1.2'
                          }}
                        >
                          {value === '' || value === null || value === undefined ? '-' : String(value)}
                        </TableCell>
                      );
                    })}

                  {/* Coluna de erro de importação */}
                  {(importing || Object.keys(importProgress).length > 0) && (
                    <TableCell sx={{
                      maxWidth: 200,
                      padding: '1px 6px',
                      fontSize: '0.65rem',
                      height: '32px',
                      lineHeight: '1.2'
                    }}>
                      {importProgress[index]?.erro && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ fontSize: '0.65rem' }}
                          title={importProgress[index].erro}
                        >
                          {importProgress[index].erro}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {parsedData.length > 0 && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Mostrando {parsedData.length} registros — {selectedCount} selecionados para importação
        </Typography>
      )}
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderAnalysisStep();
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={!apiUrl || !apiToken || loading}
            startIcon={<CloudUpload />}
          >
            {loading ? 'Buscando Dados...' : 'Buscar Dados'}
          </Button>
        );
      case 1:
        return (
          <>
            <Button
              onClick={() => setActiveStep(0)}
              disabled={importing}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={selectedCount === 0 || importing}
              startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}
              color="success"
            >
              {importing
                ? `Importando ${selectedCount} circuito(s)...`
                : importCompleted
                  ? 'Importação Concluída'
                  : `Importar ${selectedCount} circuito(s)`
              }
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Importar Circuitos via API MKAuth
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {importing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Importando circuitos...
            </Typography>
          </Box>
        )}

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>

        {getStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {getActionButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default ImportSysupcircuitosModal;